'use client';

import Icon from '@/components/ui/AppIcon';

interface ExpertiseDomain {
  icon: string;
  title: string;
  description: string;
  highlights: string[];
}

const ExpertiseSection = () => {
  const expertiseDomains: ExpertiseDomain[] = [
    {
      icon: 'ChartBarIcon',
      title: 'ESG & Carbon Accounting',
      description: 'Comprehensive ESG reporting and carbon footprint management using AI and advanced analytics',
      highlights: [
        'BRSR & CSRD Compliance',
        'GHG Protocol Implementation',
        'Scope 1, 2, 3 Assessment',
        'NetZero Roadmap Development',
        'Fugitive Emission Control',
        'Low Emission Control for Oil and Gas Industry',
      ],
    },
    {
      icon: 'CpuChipIcon',
      title: 'AI & Technology Solutions',
      description: 'Cutting-edge AI-powered tools for emission tracking, reporting automation, and sustainability analytics',
      highlights: [
        'AI-Driven ESG Reporting',
        'Automated BRSR Generation',
        'Real-time Carbon Tracking',
        'Predictive Analytics',
        'SAP IoT-SCADA Expert',
      ],
    },
    {
      icon: 'MapIcon',
      title: 'GIS & Remote Sensing',
      description: 'Advanced geospatial intelligence for environmental monitoring and sustainable development',
      highlights: [
        'Satellite Imagery Analysis',
        'Drone Mapping Solutions',
        'Custom GIS Software',
        'Defense Applications',
        'Mining Eclipse Software Based ESG',
      ],
    },
    {
      icon: 'BuildingOffice2Icon',
      title: 'Enterprise Solutions',
      description: 'Tailored sustainability strategies for enterprises, government, and defense organizations',
      highlights: [
        'Banking & Finance ESG',
        'Government Engagement',
        'Infrastructure Projects',
        'Value Chain Partnerships',
        'SAP EHS',
      ],
    },
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-4">
            Combined Expertise Domains
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our leadership team's complementary skills create a comprehensive approach to 
            sustainability, technology, and environmental intelligence.
          </p>
        </div>

        {/* Expertise Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {expertiseDomains.map((domain, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-xl p-8 hover:shadow-natural-lg transition-all duration-300 ease-organic group"
            >
              {/* Icon & Title */}
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <Icon name={domain.icon} size={28} className="text-primary group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-text-primary mb-2 group-hover:text-primary transition-colors">
                    {domain.title}
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {domain.description}
                  </p>
                </div>
              </div>

              {/* Highlights */}
              <div className="space-y-3">
                {domain.highlights.map((highlight, idx) => (
                  <div
                    key={idx}
                    className="flex items-center space-x-3 text-sm text-text-secondary"
                  >
                    <Icon name="CheckCircleIcon" size={18} className="text-success flex-shrink-0" />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-text-secondary mb-6">
            Ready to leverage our team's expertise for your sustainability goals?
          </p>
          <a
            href="https://wa.me/919901503669?text=I%20want%20to%20discuss%20sustainability%20solutions%20with%20the%20GenCarbon%20team"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-conversion text-conversion-foreground font-heading font-semibold rounded-xl hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} />
            <span>Connect with Our Team</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ExpertiseSection;